import { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardBody,
  Input,
  RadioGroup,
  Radio,
  Select,
  SelectItem,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { useRoute, useLocation } from 'wouter';
import ReportEditor from '../../../components/reports/ReportEditor';
import {
  createReport,
  updateReport,
  getReportById,
  publishReport,
} from 'common/src/api/reports';
import { ReportVisibility } from 'common/src/utils/types';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import { supabase } from 'common/src/supabase';
import Header from '../../../components/layouts/Header';
import { BackButton, LoadingState, PageLayout } from '../../../components/ui';

interface Exporter {
  id: number;
  name: string;
}

const ReportEditorPage = () => {
  const [, params] = useRoute('/superadmin-reports/edit/:id');
  const isEditMode = !!params?.id;
  const reportId = params?.id ? parseInt(params.id) : null;

  const [, setLocation] = useLocation();
  const { userProfile } = useUserStore();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [visibility, setVisibility] = useState<ReportVisibility>('public');
  const [targetAudience, setTargetAudience] = useState<Set<string>>(
    new Set(['all'])
  );
  const [exporters, setExporters] = useState<Exporter[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch exporters
  useEffect(() => {
    const fetchExporters = async () => {
      const { data, error } = await supabase
        .from('exporters')
        .select('id, name')
        .order('name');

      if (error) {
        console.error('Error fetching exporters:', error);
        return;
      }

      setExporters(data || []);
    };

    fetchExporters();
  }, []);

  // Load report if editing
  useEffect(() => {
    if (isEditMode && reportId) {
      const loadReport = async () => {
        setLoading(true);
        const report = await getReportById(reportId);
        if (report) {
          setTitle(report.title);
          setContent(report.content);
          setVisibility(report.visibility);
          setTargetAudience(new Set(report.target_audience));
        } else {
          toast.error('Report not found');
          setLocation('/superadmin-reports');
        }
        setLoading(false);
      };
      loadReport();
    }
  }, [isEditMode, reportId, setLocation]);

  const handleSave = async (andPublish: boolean = false) => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!content.trim() || content === '<p></p>') {
      toast.error('Content is required');
      return;
    }

    if (visibility === 'private' && targetAudience.size === 0) {
      toast.error('Please select target audience for private reports');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        title,
        content,
        visibility,
        target_audience: Array.from(targetAudience),
      };

      let result;

      if (isEditMode && reportId) {
        result = await updateReport(reportId, payload);
      } else {
        result = await createReport(payload, userProfile!.id);
      }

      if (result) {
        toast.success(
          `Report ${isEditMode ? 'updated' : 'created'} successfully`
        );

        // If publishing, publish the report
        if (andPublish) {
          const publishResult = await publishReport(result.id);
          if (publishResult) {
            toast.success('Report published successfully');
          }
        }

        setLocation('/superadmin-reports');
      } else {
        toast.error(`Failed to ${isEditMode ? 'update' : 'create'} report`);
      }
    } catch (error) {
      console.error('Error saving report:', error);
      toast.error('An error occurred');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <LoadingState label='Loading report...' />
    );
  }

  return (
    <PageLayout>
      <BackButton href='/superadmin-reports' />
      {/* Header */}
      <Header
        title={isEditMode ? 'Editar Newsletter' : 'Crear Newsletter'}
        actions={
          <>
            <Button
              variant='light'
              onPress={() => setLocation('/superadmin-reports')}
            >
              Cancelar
            </Button>
            <Button
              variant='flat'
              onPress={() => handleSave(false)}
              isLoading={saving}
              isDisabled={saving}
            >
              Guardar como Borrador
            </Button>
            <Button
              color='primary'
              onPress={() => handleSave(true)}
              isLoading={saving}
              isDisabled={saving}
            >
              Guardar & Publicar
            </Button>
          </>
        }
      />

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Main Editor */}
        <div className='lg:col-span-2'>
          <Card>
            <CardBody className='space-y-4'>
              <Input
                label='Título del Newsletter'
                placeholder='Ingrese el título del Newsletter'
                value={title}
                onValueChange={setTitle}
                size='lg'
                isRequired
              />

              <div>
                <label className='text-sm font-medium mb-2 block'>
                  Contenido del Newsletter
                </label>
                <ReportEditor content={content} onChange={setContent} />
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Settings Sidebar */}
        <div className='lg:col-span-1'>
          <Card>
            <CardBody className='space-y-4'>
              <h3 className='text-lg font-semibold'>
                Configuración del Newsletter
              </h3>

              <RadioGroup
                label='Visibilidad'
                value={visibility}
                onValueChange={(val) => setVisibility(val as ReportVisibility)}
              >
                <Radio value='open'>
                  <div>
                    <div className='font-medium'>🌐 Público</div>
                    <div className='text-xs text-default-500'>
                      Cualquiera con el link (no requiere login)
                    </div>
                  </div>
                </Radio>
                {/* <Radio value='private'>
                  <div>
                    <div className='font-medium'>🔒 Privado</div>
                    <div className='text-xs text-default-500'>
                      Requiere cuenta de CherryMarket
                    </div>
                  </div>
                </Radio> */}
              </RadioGroup>

              {visibility === 'private' && (
                <Select
                  label='Público Objetivo'
                  placeholder='Seleccionar exportadores'
                  selectionMode='multiple'
                  selectedKeys={targetAudience}
                  onSelectionChange={(keys) =>
                    setTargetAudience(keys as Set<string>)
                  }
                  isRequired
                >
                  <SelectItem key='all' value='all'>
                    Todos los Exportadores
                  </SelectItem>
                  {exporters.map((exporter) => (
                    <SelectItem
                      key={exporter.id.toString()}
                      value={exporter.id.toString()}
                    >
                      {exporter.name}
                    </SelectItem>
                  ))}
                </Select>
              )}

              <div className='pt-4 border-t'>
                <div className='text-sm text-default-600 space-y-2'>
                  <p className='font-medium'>Tips:</p>
                  <ul className='list-disc list-inside space-y-1 text-xs'>
                    <li>Use headings to structure your content</li>
                    <li>Add images to make it visually appealing</li>
                    <li>Use links to reference external content</li>
                    <li>Open reports are great for marketing</li>
                    <li>Private reports for client-specific data</li>
                  </ul>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default ReportEditorPage;
